// src/components/GridView/service.ts
import { apiService } from '@/services/api';
import { socketService } from '@/services/socketService';
import type {
  GridViewRequestParams,
  GridViewResponse,
  SaveFieldRequest,
  SaveMultipleFieldRequest,
  HiddenColumnsRequest,
  UpdateHiddenColumnsRequest,
  UpdateConversationTitleRequest,
  ExportSpreadSheetRequest,
} from './types';
import type { ConversationDetail } from '@/types/conversation';

export interface FieldOption {
  Id: number;
  Text: string;
  Value: string | number;
  IsSelected?: boolean;
}

class GridViewService
{
  /**
   * Get grid conversations (flat list, paginated)
   * POST api/Conversation/GetNewGridConversations
   */
  async getGridConversations(params: GridViewRequestParams): Promise<GridViewResponse>
  {
    try
    {
      const response = await apiService.post<GridViewResponse>(
        'Conversation/GetNewGridConversations',
        params,
      );
      return response ?? { Conversations: [] };
    }
    catch (error)
    {
      console.error('Error getting grid conversations:', error);
      return { Conversations: [] };
    }
  }

  /**
   * Save a single custom field value
   * PUT api/customfieldvalue/ (existing) / POST api/customfieldvalue/ (new)
   */
  async saveFieldValue(req: SaveFieldRequest): Promise<boolean>
  {
    try
    {
      const payload = { ...req, SocketConnectionId: socketService.connectionId };
      if (req.CustomFieldValueId)
      {
        await apiService.put('customfieldvalue/', payload);
      }
      else
      {
        await apiService.post('customfieldvalue/', payload);
      }
      return true;
    }
    catch (error)
    {
      console.error('Error saving field value:', error);
      return false;
    }
  }

  /**
   * Batch-save multiple custom field values.
   * POST api/CustomFieldValueMultiple/
   * Payload: { customFields: [...], types: [...] }
   * types can be 1 (new), 2 (existing), or "savePost"/"saveUpdate" (legacy string format for type-7 persons)
   */
  async saveMultipleFieldValues(
    reqs: SaveMultipleFieldRequest[],
    types?: (1 | 2 | string)[],
  ): Promise<boolean>
  {
    try
    {
      const resolvedTypes = types ?? reqs.map(r => ((r.CustomFieldValueId ?? 0) > 0 ? 2 : 1));

      const payload = {
        customFields: reqs,
        types: resolvedTypes,
        SocketConnectionId: socketService.connectionId,
      };

      await apiService.post('CustomFieldValueMultiple/', payload);

      return true;
    }
    catch (error)
    {
      console.error('[GridView Service] Error saving multiple field values:', error);
      return false;
    }
  }

  /**
   * Get hidden columns configuration for the grid.
   * GET api/Conversation/GetGridHiddenColumn
   * Response is a CSV string like "conversationId,37_12" — parsed into string[].
   */
  async getHiddenColumns(req: HiddenColumnsRequest): Promise<string[]>
  {
    try
    {
      const qs = new URLSearchParams();
      if (req.dashboardId != null) qs.set('dashboardId', String(req.dashboardId));
      if (req.projectId != null) qs.set('projectId', String(req.projectId));
      if (req.configValueId != null) qs.set('configValueId', String(req.configValueId));

      const raw = await apiService.get<string>(`Conversation/GetGridHiddenColumn?${qs.toString()}`);
      return raw
        ? raw
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
        : [];
    }
    catch (error)
    {
      console.error('Error getting hidden columns:', error);
      return [];
    }
  }

  /**
   * Update hidden columns configuration
   * POST api/Conversation/UpdateGridHiddenColumn
   */
  async updateHiddenColumns(req: UpdateHiddenColumnsRequest): Promise<boolean>
  {
    try
    {
      await apiService.post('Conversation/UpdateGridHiddenColumn', req);
      return true;
    }
    catch (error)
    {
      console.error('Error updating hidden columns:', error);
      return false;
    }
  }

  /**
   * Update a conversation's title
   * PUT api/conversation/updateconversationtitle
   */
  async updateConversationTitle(req: UpdateConversationTitleRequest): Promise<boolean>
  {
    try
    {
      await apiService.put('conversation/updateconversationtitle', {
        ...req,
        SocketConnectionId: socketService.connectionId,
      });
      return true;
    }
    catch (error)
    {
      console.error('Error updating conversation title:', error);
      return false;
    }
  }

  /**
   * Fetch combo / multi-select options for a custom field.
   * GET api/customField/GetListComboBox?customFieldId=X[&configValueId=Y&layerId=Z&projectId=W]
   */
  async getFieldOptions(
    customFieldId: number,
    params?: { configValueId?: number; layerId?: number; projectId?: number },
  ): Promise<FieldOption[]>
  {
    try
    {
      const qs = new URLSearchParams({ customFieldId: String(customFieldId) });
      if (params?.configValueId != null) qs.set('configValueId', String(params.configValueId));
      if (params?.layerId != null) qs.set('layerId', String(params.layerId));
      if (params?.projectId != null) qs.set('projectId', String(params.projectId));

      const data = await apiService.get<FieldOption[]>(
        `customField/GetListComboBox?${qs.toString()}`,
      );
      return data ?? [];
    }
    catch (error)
    {
      console.error('Error fetching field options:', error);
      return [];
    }
  }

  /**
   * Fetch options for a type-7 (person multi-select) field using the field's ServiceUrl.
   * GET api/{serviceUrl}?configValueId=X&layerId=Y&projectId=Z
   * Returns FieldOption[] mapped via textField/valueField.
   */
  async getFieldOptionsByServiceUrl(
    serviceUrl: string,
    params: { configValueId?: number; layerId?: number; projectId?: number },
    textField = 'Name',
    valueField = 'Id',
  ): Promise<FieldOption[]>
  {
    try
    {
      let url = serviceUrl.replace(/^api\//, '');
      const qs = new URLSearchParams();
      if (params.configValueId != null) qs.set('configValueId', String(params.configValueId));
      if (params.layerId != null) qs.set('layerId', String(params.layerId));
      if (params.projectId != null) qs.set('projectId', String(params.projectId));
      if (qs.toString())
      {
        url = url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;
      }

      const data = await apiService.get<any[]>(url);
      return (data ?? []).map(item => ({
        Id: item[valueField],
        Text: item[textField],
        Value: item[valueField],
      }));
    }
    catch (error)
    {
      console.error('Error fetching field options by service URL:', error);
      return [];
    }
  }

  /**
   * Toggle red flag on a conversation
   * PUT api/conversation/MarkRedFlag
   */
  async markRedFlag(conversation: ConversationDetail, isRedFlag: boolean): Promise<boolean>
  {
    try
    {
      await apiService.put('conversation/MarkRedFlag', {
        conversations: [
          {
            ConfigValueId: conversation.ConfigValueId,
            ConversationId: conversation.ConversationId,
            CoreTaskId: conversation.CoreTaskId,
            CreatedByID: conversation.CreatedByID,
            Icon: conversation.Icon,
            IsRedFlag: isRedFlag,
            ProjectId: conversation.ProjectId,
            Title: conversation.Title,
            callService: true,
          },
        ],
        SocketConnectionId: socketService.connectionId,
      });
      return true;
    }
    catch (error)
    {
      console.error('Error marking red flag:', error);
      return false;
    }
  }

  /**
   * Toggle important on a conversation
   * PUT api/conversation/MarkImportant
   */
  async markImportant(
    conversation: ConversationDetail,
    isImportant: boolean | null,
  ): Promise<boolean>
  {
    try
    {
      await apiService.put('conversation/MarkImportant', {
        conversations: [
          {
            ConfigValueId: conversation.ConfigValueId,
            ConversationId: conversation.ConversationId,
            CoreTaskId: conversation.CoreTaskId,
            CreatedByID: conversation.CreatedByID,
            Icon: conversation.Icon,
            IsImportant: isImportant ?? false,
            ProjectId: conversation.ProjectId,
            Title: conversation.Title,
            callService: true,
          },
        ],
        SocketConnectionId: socketService.connectionId,
      });
      return true;
    }
    catch (error)
    {
      console.error('Error marking important:', error);
      return false;
    }
  }

  /**
   * Get conversations for a specific person in ME View
   * POST api/Conversation/GetViewMeDetailInfo
   */
  async getMeViewConversations(params: GridViewRequestParams): Promise<GridViewResponse>
  {
    try
    {
      const response = await apiService.post<{ Conversations: ConversationDetail[]; MeView: any }>(
        'Conversation/GetViewMeDetailInfo',
        params,
      );
      const items = response?.Conversations ?? [];
      // No real total count from this endpoint. Leave TotalItems undefined so the
      // page-size heuristic drives pagination (setting it to items.length would
      // stop paging after the first full page).
      return { Conversations: items };
    }
    catch (error)
    {
      console.error('Error getting ME view conversations:', error);
      return { Conversations: [] };
    }
  }

  /**
   * Export grid data as spreadsheet
   * POST api/Conversation/ExportSpreadSheet
   */
  async exportSpreadSheet(params: ExportSpreadSheetRequest): Promise<ConversationDetail[]>
  {
    try
    {
      const response = await apiService.post<ConversationDetail[]>(
        'Conversation/ExportSpreadSheet',
        params,
      );
      return response ?? [];
    }
    catch (error)
    {
      console.error('Error exporting spreadsheet:', error);
      return [];
    }
  }
}

export const gridViewService = new GridViewService();
